import { useState, useCallback } from 'react'
import { Row, SortingState, Table } from '@atbay/cyber-components'
import { getTableRowContext } from '@/ui/utils/tableUtils'
import { useDrawer } from '@core/context/DrawerContext'
import { useBreakpoints } from '@/core/hooks/useBreakpoints/useBreakpoints'
import { Analytics } from '@core/services/mixpanel'
import { AccountModule } from '@/core/modules/account/account.module'
import { TAlert, TIncident } from '@/core/modules/mdr/cases'

export type TCurrentTab = 'incidents' | 'alerts'

interface UseCasesTableProps {
    cases: TIncident[] | TAlert[]
    currentTab: TCurrentTab
}

const TABLE_TITLE_MAP = {
    incidents: 'MDR Incidents',
    alerts: 'MDR Alerts',
} as const

export const useCasesTable = ({ cases, currentTab }: UseCasesTableProps) => {
    const [sorting, setSorting] = useState<SortingState>([])
    const { setDrawerState, drawerState } = useDrawer()
    const { belowBreakpoint } = useBreakpoints()
    const { isMdrActive } = AccountModule.useStore()

    const openDrawer = useCallback(
        (row: Row<TIncident | TAlert>, table: Table<TIncident | TAlert>) => {
            Analytics.event(
                currentTab === 'alerts'
                    ? 'SEM::mdr_alerts_details_drawer_open'
                    : 'SEM::mdr_incident_details_drawer_open',
                {
                    isMdrActive,
                    origin: `mdr_${currentTab}_table`,
                }
            )
            const { sortedIndex, items: sortedCases } = getTableRowContext(row, table)

            setDrawerState((prevState: any) => ({
                ...prevState,
                isDrawerOpen: true,
                drawerContentType: 'caseDetails',
                contentProps: {
                    width: belowBreakpoint('xl')
                        ? '73vw'
                        : belowBreakpoint('xxl')
                          ? '68vw'
                          : '55vw',
                    numberOfItems: sortedCases.length,
                    currentIndex: sortedIndex,
                    cases: sortedCases,
                    currentTab,
                    withNavigation: true,
                },
            }))
        },
        [currentTab, setDrawerState, isMdrActive, belowBreakpoint]
    )

    const emptyStateText =
        currentTab === 'incidents'
            ? 'No incidents found'
            : currentTab === 'alerts'
              ? 'No alerts found'
              : 'No results found'

    const tableTitle = TABLE_TITLE_MAP[currentTab]

    return {
        sorting,
        setSorting,
        emptyStateText,
        activeCaseNumber: drawerState.isDrawerOpen
            ? drawerState.contentProps.cases?.[
                  drawerState.contentProps.currentIndex
              ]?.caseNumber
            : undefined,
        openDrawer,
        tableTitle,
    }
}
